import { useMemo } from 'react'

import type { NavItem } from './menuItems'

import type { FeatureAccessUser } from '@helpers/featureAccess'
import { getFeatureAccess } from '@helpers/featureAccess'
import type { Account } from '@models/Account'
import routes from '@src/Routes/routes'
import { Perm } from '@type/permissions'

// Narrow shapes that capture exactly what filterNavItems reads. Used
// in the function signature below so test fixtures can build minimal
// objects without `as unknown as Account` casts. Production callers
// pass full Account / NavItem[]; those satisfy the Pick types
// structurally.
//
// `features` is the backend feature registry resolved for this viewer
// (one state per key); `permissions` is read only for the action-level
// `requirePerm` refinement and the Workflows destination. There is no
// admin tier, no raw feature matrix and no team setting here: every one
// of those inputs is composed server side.
export type NavFilterUser = FeatureAccessUser & Pick<Account, 'permissions'>
// Deliberately not a Pick of NavItem: the Teams shell runs its own entry
// shapes (JSX labels, isActive matchers) through this SAME filter, which
// is what makes web/Teams parity structural rather than a test.
export type NavFilterItem = Pick<NavItem, 'to' | 'feature' | 'requirePerm'>

/** A nav item after filtering, with the paywall state the shell renders. */
export type FilteredNavItem<T extends NavFilterItem> = T & {
  locked: boolean
  paywallPath?: string
}

export interface FilterNavItemsOptions {
  /**
   * Keep entries the viewer's plan locks, flagged `locked` with their
   * paywall path. The sidebars want them (locked, pointing at the paywall
   * — the upsell surface); the landing route and the quick-action menu
   * do not.
   */
  includeLocked?: boolean
}

export const useWindowControlsOverlay = () => {
  return useMemo(() => {
    return navigator.windowControlsOverlay !== undefined && navigator.windowControlsOverlay.visible
  }, [navigator.windowControlsOverlay?.visible])
}

/**
 * Where the Workflows nav entry should point for this viewer.
 *
 * The entry is a CONTAINER: Custom lists is a tab of that section with its
 * own codename, so the registry shows it to a role holding only
 * `custom_list.view`. Its destination has to follow, or the entry appears
 * and then bounces them back out — `/workflows` requires `workflow.view`.
 *
 * Exported for the Teams shell, which builds its nav independently. Web
 * callers get this applied for them by `filterNavItems` and should not call
 * it directly.
 */
export const workflowsSectionDestination = (permissions?: string[] | null): string =>
  (permissions || []).includes(Perm.WORKFLOW_VIEW) ? routes.private.pipelines : routes.private.customLists

/**
 * Visible nav items for this user, with each item's destination resolved.
 *
 * A `usable` state in `/my-account.features` is the gate; a `locked`
 * state renders the entry locked when asked (see `includeLocked`); a
 * `hidden` or absent key never shows. The optional `requirePerm` is an
 * action-level refinement on top, for quick actions that create something
 * inside a feature.
 *
 * It filters AND rewrites, which the name undersells. That is deliberate:
 * the destination is a function of the same permissions the filter reads,
 * and every consumer wants it applied. Leaving it to callers meant three
 * consumers of NAV_ITEMS (LeftNav, BurgerMenu, getLandingRoute) each had to
 * remember, and two of them did not — getLandingRoute is what
 * ProtectedRoute redirects to when a perm check fails, so handing back
 * `/workflows` for a role without `workflow.view` was an infinite redirect
 * rather than a wrong page.
 */
export const filterNavItems = <T extends NavFilterItem>(
  items: T[],
  user?: NavFilterUser,
  showNavsWithRestriction?: Record<string, boolean>,
  { includeLocked = false }: FilterNavItemsOptions = {},
): FilteredNavItem<T>[] => {
  const perms = new Set(user?.permissions ?? [])

  const visible: FilteredNavItem<T>[] = []
  for (const item of items) {
    const isAllowed =
      item.to && showNavsWithRestriction?.[item.to] !== undefined ? showNavsWithRestriction[item.to] : true
    if (!isAllowed) continue

    const access = getFeatureAccess(item.feature, user)
    if (!access.usable && !(includeLocked && access.locked)) continue

    if (item.requirePerm !== undefined && !perms.has(item.requirePerm)) continue

    visible.push({ ...item, locked: access.locked, paywallPath: access.paywallPath })
  }

  // A no-op for every other item list — none of AP/AR/CONTRACT/ORGANIZATION/
  // QUICK_ACTIONS points at the workflows route.
  return visible.map((item) =>
    item.to === routes.private.pipelines ? { ...item, to: workflowsSectionDestination(user?.permissions) } : item,
  )
}
